import { useEffect, useRef } from 'react'
import mapboxgl, { Map, MapLayerMouseEvent } from 'mapbox-gl'
import { Feature, FeatureCollection, Point, Polygon, Position } from 'geojson'
import * as turf from '@turf/turf'
import { toast } from '@app/components/ui/Toast/toast'

const SOURCE_ID = 'documentsDrawSource'
const POINTS_LAYER_ID = 'documentsDrawPointsLayer'
const LAST_POINT_LAYER_ID = 'documentsDrawLastPointLayer'
const LINE_LAYER_ID = 'documentsDrawLineLayer'
const AREA_LAYER_ID = 'documentsDrawAreaLayer'

// Ported from SelectionTool.tsx's polygon-drawing algorithm (click to place a vertex, click the
// last vertex again to close the loop, reject self-intersecting shapes via turf.kinks) —
// adapted to per-instance refs instead of that tool's module-level state, and a plain
// onComplete callback instead of dispatching into Redux. Vertex dragging isn't ported;
// clicking an already-placed point to remove it covers the essential case.
export function useDrawPolygon(
  map: Map | null,
  active: boolean,
  resetSignal: number,
  onComplete: (ring: Position[]) => void,
) {
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  useEffect(() => {
    if (!map || !active) return

    let points: Feature[] = []
    const featureCollection: FeatureCollection = { type: 'FeatureCollection', features: [] }

    map.addSource(SOURCE_ID, { type: 'geojson', data: featureCollection })
    map.addLayer({
      id: LINE_LAYER_ID,
      type: 'line',
      source: SOURCE_ID,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#0B2948', 'line-width': 2.5 },
      filter: ['in', '$type', 'LineString', 'Polygon'],
    })
    map.addLayer({
      id: AREA_LAYER_ID,
      type: 'fill',
      source: SOURCE_ID,
      paint: { 'fill-color': '#0B2948', 'fill-opacity': 0.1 },
      filter: ['in', '$type', 'Polygon'],
    })
    map.addLayer({
      id: POINTS_LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      paint: { 'circle-radius': 5, 'circle-color': '#0B2948' },
      filter: ['in', '$type', 'Point'],
    })
    map.addLayer({
      id: LAST_POINT_LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      paint: {
        'circle-radius': 7,
        'circle-stroke-color': '#00f',
        'circle-stroke-width': 2,
        'circle-opacity': 0,
      },
      filter: ['==', ['get', 'last'], true],
    })

    const render = () => {
      points.forEach((p, i) => {
        p.properties = { ...p.properties, last: i === points.length - 1 }
      })

      let extra: Feature[] = []
      if (points.length === 2) {
        const coords = points.map((p) => (p.geometry as Point).coordinates)
        extra = [
          {
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: coords },
          },
        ]
      } else if (points.length > 2) {
        const coords = points.map((p) => (p.geometry as Point).coordinates)
        extra = [
          {
            type: 'Feature',
            properties: {},
            geometry: { type: 'Polygon', coordinates: [[...coords, coords[0]]] },
          },
        ]
      }
      featureCollection.features = [...points, ...extra]
      const source = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
      source?.setData(featureCollection)
    }

    const handleClick = (e: MapLayerMouseEvent) => {
      const clicked = map.queryRenderedFeatures(e.point, { layers: [POINTS_LAYER_ID] })
      if (clicked.length) {
        const id = clicked[0].properties?.id
        const lastId = points[points.length - 1]?.properties?.id
        if (id === lastId && points.length > 2) {
          const coords = points.map((p) => (p.geometry as Point).coordinates)
          const ring = [...coords, coords[0]]
          const polygon: Feature<Polygon> = {
            type: 'Feature',
            properties: {},
            geometry: { type: 'Polygon', coordinates: [ring] },
          }
          if (turf.kinks(polygon).features.length > 0) {
            toast({
              description: 'Please adjust the area to remove overlapping lines',
              type: 'info',
            })
            return
          }
          onCompleteRef.current(ring)
          return
        }
        points = points.filter((p) => p.properties?.id !== id)
      } else {
        points = [
          ...points,
          {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [e.lngLat.lng, e.lngLat.lat] },
            properties: { id: String(Date.now()) },
          },
        ]
      }
      render()
    }

    map.getCanvas().style.cursor = 'crosshair'
    map.on('click', handleClick)

    return () => {
      map.off('click', handleClick)
      map.getCanvas().style.cursor = ''
      ;[POINTS_LAYER_ID, LAST_POINT_LAYER_ID, LINE_LAYER_ID, AREA_LAYER_ID].forEach((id) => {
        if (map.getLayer(id)) map.removeLayer(id)
      })
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
    }
    // resetSignal has no read in the effect body — incrementing it (the Clear button, while
    // actively drawing) forces this effect to tear down and re-run, wiping any in-progress,
    // not-yet-completed vertices the same way toggling `active` off and back on would.
  }, [map, active, resetSignal])
}
